import NumberFlow from "@number-flow/react";
import clsx from "clsx/lite";
import { Minus, Plus } from "lucide-react";
import * as React from "react";
import type { FieldValues, Path } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";

import { ui } from "@patient-watch/ui";
import { cn } from "@patient-watch/ui";

interface NumberFlowBaseProps {
    label?: string;
    disabled?: boolean;
    tabIndex?: number;
    min?: number;
    max?: number;
    step?: number;
    fractionDigits?: number;
    /**
     * When true, clearing the input commits null (measurement fields).
     * Default false keeps schedule/interval behaviour: empty restores the last value.
     */
    allowEmpty?: boolean;
    /**
     * Shown inline under the box when the value is outside min and max. When set, a typed value
     * outside the range is kept (so the form can check it on Next) instead of being pulled back in.
     * The plus and minus buttons still stop at min and max.
     */
    validationMessage?: string | null;
    className?: string;
    /** Tighter control for dense tables. */
    compact?: boolean;
    /** Fires after the value changes (plus / minus / typed). */
    onValueChange?: (value: number | null) => void;
    "aria-label"?: string;
    /** Test id on the number box (for end-to-end tests). */
    "data-testid"?: string;
}

interface NumberFlowRhfProps<T extends FieldValues> extends NumberFlowBaseProps {
    name: Path<T>;
    value?: never;
    onChange?: never;
}

interface NumberFlowControlledProps extends NumberFlowBaseProps {
    name?: never;
    value: number | null;
    onChange: (value: number | null) => void;
}

export type NumberFlowProps<T extends FieldValues> = NumberFlowRhfProps<T> | NumberFlowControlledProps;

function NumberFlowControl({
    value,
    onChange,
    disabled = false,
    tabIndex,
    min = 0,
    max = Infinity,
    step = 1,
    fractionDigits = 0,
    allowEmpty = false,
    validationMessage = null,
    compact = false,
    onValueChange,
    name,
    "aria-label": ariaLabel,
    "data-testid": testId,
}: {
    value: number | null;
    onChange: (value: number | null) => void;
    disabled?: boolean;
    tabIndex?: number;
    min?: number;
    max?: number;
    step?: number;
    fractionDigits?: number;
    allowEmpty?: boolean;
    validationMessage?: string | null;
    compact?: boolean;
    onValueChange?: (value: number | null) => void;
    name?: string;
    "aria-label"?: string;
    "data-testid"?: string;
}) {
    const fallbackValue = React.useRef(value ?? Math.max(min, 0));
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [animated, setAnimated] = React.useState(true);
    const [showCaret, setShowCaret] = React.useState(true);
    const [focused, setFocused] = React.useState(false);
    const [draft, setDraft] = React.useState("");

    React.useEffect(() => {
        if (value !== null) fallbackValue.current = value;
    }, [value]);

    const commit = (next: number | null) => {
        onChange(next);
        onValueChange?.(next);
    };

    const formatCommitted = (next: number | null) => {
        if (next === null) return "";
        return String(next);
    };

    const parseDraft = (raw: string): number | null => {
        if (raw.trim() === "") return null;
        const next = Number(raw);
        return Number.isFinite(next) ? next : Number.NaN;
    };

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = () => {
        setFocused(true);
        setAnimated(false);
        setShowCaret(true);
        setDraft(formatCommitted(value));
    };

    const handleInput: React.InputEventHandler<HTMLInputElement> = ({ currentTarget: el }) => {
        setAnimated(false);
        const raw = el.value;

        if (focused) {
            setDraft(raw);
            if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
                if (allowEmpty && raw === "") commit(null);
                return;
            }
            // Keep incomplete decimals (e.g. "12.") in the draft only.
            if (/[.,]$/.test(raw)) return;
            const next = parseDraft(raw);
            if (next === null) {
                if (allowEmpty) commit(null);
                return;
            }
            if (!Number.isNaN(next) && (validationMessage || (min <= next && next <= max))) commit(next);
            return;
        }

        if (raw === "") {
            if (allowEmpty) {
                commit(null);
                return;
            }
            const restored = fallbackValue.current;
            el.value = String(restored);
            commit(restored);
            return;
        }
        const next = el.valueAsNumber;
        if (Number.isFinite(next) && (validationMessage || (min <= next && next <= max))) commit(next);
    };

    const handlePointerDown = (diff: number) => (event: React.PointerEvent<HTMLButtonElement>) => {
        setAnimated(true);
        if (event.pointerType === "mouse") {
            event.preventDefault();
            inputRef.current?.focus();
        }
        const current = value ?? Math.max(min, 0);
        const newVal = Math.min(Math.max(current + diff * step, min), max);
        if (newVal === current) return;
        commit(newVal);
        if (focused) setDraft(formatCommitted(newVal));
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = ({ currentTarget: el }) => {
        setFocused(false);
        const raw = focused ? draft : el.value;
        const num = parseDraft(raw);

        if (num === null || Number.isNaN(num)) {
            if (allowEmpty) {
                commit(null);
                setDraft("");
                return;
            }
            const restored = value ?? fallbackValue.current;
            commit(restored);
            setDraft(formatCommitted(restored));
            return;
        }

        if (validationMessage) {
            commit(num);
            setDraft(formatCommitted(num));
            return;
        }

        const clamped = Math.min(Math.max(num, min), max);
        commit(clamped);
        setDraft(formatCommitted(clamped));
    };

    const inputValue = focused ? draft : formatCommitted(value);
    const showOverlay = !focused && value !== null;

    // The number on screen: the draft while typing, otherwise the committed value.
    const shownValue = focused ? parseDraft(draft) : value;
    const invalid = shownValue !== null && !Number.isNaN(shownValue) && (shownValue < min || shownValue > max);
    const rangeMessage = Number.isFinite(max)
        ? `Enter a number between ${min} and ${max}.`
        : `Enter a number of at least ${min}.`;

    const textSize = compact ? "text-sm" : "text-lg";
    const buttonClass = cn(
        "flex justify-center items-center disabled:opacity-50 text-slate-600 hover:text-slate-900 active:bg-slate-300 dark:text-slate-300 dark:hover:text-white dark:active:bg-slate-600 disabled:cursor-not-allowed disabled:hover:bg-slate-100 dark:disabled:hover:bg-slate-800 disabled:hover:text-slate-600 dark:disabled:hover:text-slate-300",
        compact
            ? "px-1.5 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800"
            : "px-4 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
    );

    return (
        <div className="w-full">
            <div
                className={cn(
                    "flex items-stretch bg-white dark:bg-slate-900 disabled:opacity-50 border rounded-md ring-offset-white focus-within:ring-2 focus-within:ring-offset-2 dark:ring-offset-slate-900 w-full overflow-hidden disabled:cursor-not-allowed",
                    invalid
                        ? "border-red-500 focus-within:ring-red-300"
                        : "border-slate-300 dark:border-slate-500 focus-within:ring-indigo-300",
                    compact ? "h-10" : "h-14"
                )}
            >
                <button
                    type="button"
                    aria-label="Decrease"
                    tabIndex={-1}
                    className={cn(buttonClass, !compact && "border-r border-slate-300 dark:border-slate-500")}
                    disabled={disabled || (value !== null && min != null && value <= min)}
                    onPointerDown={handlePointerDown(-1)}
                >
                    <Minus className={compact ? "size-4" : "size-6"} absoluteStrokeWidth strokeWidth={2.5} />
                </button>
                <div className="relative flex flex-1 justify-center items-center min-w-[1.5rem]">
                    <input
                        ref={inputRef}
                        className={clsx(
                            showCaret ? "caret-indigo-500" : "caret-transparent",
                            showOverlay ? "text-transparent" : "text-slate-900 dark:text-slate-100",
                            textSize,
                            "w-full bg-transparent py-2 text-center font-medium outline-none disabled:cursor-not-allowed border-0 focus:outline-none focus:ring-0 focus:border-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none selection:bg-indigo-200 dark:selection:bg-indigo-700"
                        )}
                        style={{ fontKerning: "none" }}
                        type="text"
                        inputMode="decimal"
                        role="spinbutton"
                        autoComplete="off"
                        value={inputValue}
                        onFocus={handleFocus}
                        onInput={handleInput}
                        onBlur={handleBlur}
                        disabled={disabled}
                        tabIndex={tabIndex}
                        data-cy={name}
                        aria-label={ariaLabel}
                        data-testid={testId}
                        aria-invalid={invalid || undefined}
                        aria-valuemin={min}
                        aria-valuemax={Number.isFinite(max) ? max : undefined}
                        aria-valuenow={value ?? undefined}
                    />
                    {showOverlay && (
                        <NumberFlow
                            value={value}
                            locales="en-US"
                            format={{
                                useGrouping: false,
                                minimumFractionDigits: 0,
                                maximumFractionDigits: fractionDigits,
                            }}
                            aria-hidden="true"
                            animated={animated}
                            onAnimationsStart={() => setShowCaret(false)}
                            onAnimationsFinish={() => setShowCaret(true)}
                            className={cn(
                                "absolute inset-0 flex justify-center items-center font-medium text-slate-900 dark:text-slate-100 pointer-events-none",
                                textSize
                            )}
                            willChange
                        />
                    )}
                </div>
                <button
                    type="button"
                    aria-label="Increase"
                    tabIndex={-1}
                    className={cn(buttonClass, !compact && "border-l border-slate-300 dark:border-slate-500")}
                    disabled={disabled || (value !== null && max != null && value >= max)}
                    onPointerDown={handlePointerDown(1)}
                >
                    <Plus className={compact ? "size-4" : "size-6"} absoluteStrokeWidth strokeWidth={2.5} />
                </button>
            </div>
            {invalid && (
                <p role="alert" className="mt-1 font-medium text-red-600 dark:text-red-400 text-sm">
                    {validationMessage || rangeMessage}
                </p>
            )}
        </div>
    );
}

export default function NumberFlowInput<T extends FieldValues>(props: NumberFlowProps<T>) {
    const {
        label,
        className,
        compact,
        onValueChange,
        disabled,
        tabIndex,
        min,
        max,
        step,
        fractionDigits,
        allowEmpty,
        validationMessage,
    } = props;

    // Controlled mode is not a form field: FormLabel/FormControl need a react-hook-form context and crash without one.
    if ("value" in props && props.value !== undefined && props.onChange) {
        return (
            <ui.FormItem className={className}>
                {!!label && <ui.Label>{label}</ui.Label>}
                <NumberFlowControl
                    value={props.value}
                    onChange={props.onChange}
                    disabled={disabled}
                    tabIndex={tabIndex}
                    min={min}
                    max={max}
                    step={step}
                    fractionDigits={fractionDigits}
                    allowEmpty={allowEmpty}
                    validationMessage={validationMessage}
                    compact={compact}
                    onValueChange={onValueChange}
                    aria-label={props["aria-label"]}
                    data-testid={props["data-testid"]}
                />
            </ui.FormItem>
        );
    }

    const name = props.name as Path<T>;
    const { control } = useFormContext<T>();
    const {
        field: { value, onChange },
        fieldState: { error },
    } = useController({ name, control });

    return (
        <ui.FormItem className={className}>
            {!!label && <ui.FormLabel>{label}</ui.FormLabel>}
            <ui.FormControl>
                <NumberFlowControl
                    value={value === null || value === undefined || value === "" ? null : Number(value)}
                    onChange={onChange}
                    disabled={disabled}
                    tabIndex={tabIndex}
                    min={min}
                    max={max}
                    step={step}
                    fractionDigits={fractionDigits}
                    allowEmpty={allowEmpty}
                    validationMessage={validationMessage}
                    compact={compact}
                    onValueChange={onValueChange}
                    name={String(name)}
                    aria-label={props["aria-label"]}
                    data-testid={props["data-testid"]}
                />
            </ui.FormControl>
            {error && <ui.FormMessage />}
        </ui.FormItem>
    );
}
